import React, { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import {
  ContactPageContainer,
  Section,
  SectionTitle,
  TwoColumn,
  TextBlock,
  MapBlock,
  SocialLinks,
  SocialLinkItem,
} from '../styles/ContactPageStyles';

const CACHE_KEY = 'contact_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID?.trim();
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID?.trim();
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.trim();

const getEmailJsErrorMessage = (err) => {
  const rawMessage = err && typeof err.text === 'string'
    ? err.text
    : err && typeof err.message === 'string'
      ? err.message
      : '';

  const normalizedMessage = rawMessage.trim();

  if (/service id not found/i.test(normalizedMessage)) {
    return 'Email delivery is not configured correctly. Check that the EmailJS service ID exists and belongs to the same EmailJS account as the public key.';
  }

  if (/template id not found/i.test(normalizedMessage)) {
    return 'Email delivery is not configured correctly. Check that the EmailJS template ID exists for the configured EmailJS account.';
  }

  if (/public key/i.test(normalizedMessage) || /user id/i.test(normalizedMessage)) {
    return 'Email delivery is not configured correctly. Check the EmailJS public key in the frontend environment settings.';
  }

  return normalizedMessage || 'Failed to send message.';
};

const ContactPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formValues, setFormValues] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState({ sending: false, success: null, error: null });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/contact');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load contact page data.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const contactInfo = pageData ? pageData.contactInfo : null;
  const location = pageData ? pageData.location : null;
  const socialLinks = pageData && Array.isArray(pageData.socialLinks) ? pageData.socialLinks : [];
  const header = pageData && pageData.header ? pageData.header : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setFormStatus({
        sending: false,
        success: null,
        error: 'Email delivery is not configured. Add valid EmailJS service ID, template ID, and public key values to the frontend environment.',
      });
      return;
    }

    setFormStatus({ sending: true, success: null, error: null });

    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, formValues, PUBLIC_KEY);
      setFormStatus({ sending: false, success: 'Message sent successfully.', error: null });
      setFormValues({ name: '', email: '', message: '' });
    } catch (err) {
      setFormStatus({
        sending: false,
        success: null,
        error: getEmailJsErrorMessage(err),
      });
    }
  };

  if (loading && !pageData) {
    return (
      <ContactPageContainer>
        <LoadingSpinner />
      </ContactPageContainer>
    );
  }

  if (error && !pageData) {
    return <ContactPageContainer>{error}</ContactPageContainer>;
  }

  return (
    <ContactPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      <Section>
        <SectionTitle>Contact Information</SectionTitle>
        <TwoColumn>
          <TextBlock>
            {contactInfo && contactInfo.address && (
              <p><strong>Address:</strong> {contactInfo.address}</p>
            )}
            {contactInfo && contactInfo.phone && (
              <p><strong>Phone:</strong> {contactInfo.phone}</p>
            )}
            {contactInfo && contactInfo.email && (
              <p><strong>Email:</strong> {contactInfo.email}</p>
            )}
            {contactInfo && contactInfo.website && (
              <p><strong>Website:</strong> {contactInfo.website}</p>
            )}

            {socialLinks.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h3>Social Media</h3>
                <SocialLinks>
                  {socialLinks.map((link, index) => (
                    <SocialLinkItem key={`${link.platform || 'social'}-${index}`}>
                      {link.url ? (
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.platform || link.url}
                        </a>
                      ) : (
                        link.platform
                      )}
                    </SocialLinkItem>
                  ))}
                </SocialLinks>
              </div>
            )}
          </TextBlock>

          <MapBlock>
            {location && location.mapEmbedCode && (
              <div
                dangerouslySetInnerHTML={{ __html: location.mapEmbedCode }}
              />
            )}
            {!location?.mapEmbedCode && location?.mapUrl && (
              <TextBlock>
                <a href={location.mapUrl} target="_blank" rel="noreferrer">
                  View map
                </a>
              </TextBlock>
            )}
          </MapBlock>
        </TwoColumn>
      </Section>

      <Section>
        <SectionTitle>Send a Message</SectionTitle>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label>
              Name
              <br />
              <input
                type="text"
                name="name"
                value={formValues.name}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
                required
              />
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>
              Email
              <br />
              <input
                type="email"
                name="email"
                value={formValues.email}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4 }}
                required
              />
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>
              Message
              <br />
              <textarea
                name="message"
                value={formValues.message}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, marginTop: 4, minHeight: 120 }}
                required
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={formStatus.sending}
            style={{
              padding: '10px 18px',
              backgroundColor: '#d4af37',
              color: '#000000',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {formStatus.sending ? 'Sending...' : 'Send Message'}
          </button>
          {formStatus.error && (
            <p style={{ color: 'red', marginTop: 8 }}>{formStatus.error}</p>
          )}
          {formStatus.success && (
            <p style={{ color: 'green', marginTop: 8 }}>{formStatus.success}</p>
          )}
        </form>
      </Section>
    </ContactPageContainer>
  );
};

export default ContactPage;
